/**
 * Files attached to a user message. Copied from the web app, with the
 * TanStack mutation for download links replaced by a direct call.
 */
import { File } from "@/agents-v2/components/assistant-ui/file";
import { useAgentsChat } from "@/agents-v2/components/common/chat-provider";
import { createUploadedFileLink, type AgentUploadedFile } from "@/agents-v2/api";
import {
  AGENT_UPLOADED_FILES_METADATA_KEY,
  getUploadedFilesFromMetadata,
  openDownloadUrl,
} from "@/agents-v2/uploads/uploaded-files";
import { cn } from "@/agents-v2/utils/cn";
import { MessagePrimitive, useAuiState } from "@assistant-ui/react";
import { useMemo, useState, type FC, type MouseEvent, type ReactNode } from "react";
import { useAuthHeader } from "react-auth-kit";

export const UserMessageFiles: FC<{ leading?: ReactNode }> = ({ leading }) => {
  const hasFileParts = useAuiState((state) => state.message.parts.some((part) => part.type === "file"));
  const uploadedFileRecordsJson = useAuiState((state) =>
    JSON.stringify(state.message.metadata?.custom?.[AGENT_UPLOADED_FILES_METADATA_KEY] ?? null)
  );
  const uploadedFiles = useMemo(() => {
    let uploadedFileRecords: unknown = null;
    try {
      uploadedFileRecords = JSON.parse(uploadedFileRecordsJson);
    } catch {
      uploadedFileRecords = null;
    }
    return getUploadedFilesFromMetadata({
      [AGENT_UPLOADED_FILES_METADATA_KEY]: uploadedFileRecords,
    });
  }, [uploadedFileRecordsJson]);

  if (!leading && !hasFileParts && uploadedFiles.length === 0) return null;

  return (
    <div className="aui-user-message-file-parts flex max-w-full flex-wrap justify-end gap-2 empty:hidden">
      {leading}
      {uploadedFiles.length === 0 ? (
        <MessagePrimitive.Parts>{({ part }) => (part.type === "file" ? <File {...part} /> : null)}</MessagePrimitive.Parts>
      ) : null}
      {uploadedFiles.map((file) => (
        <UploadedFileRow key={`${file.filename}:${file.path}`} file={file} />
      ))}
    </div>
  );
};

const UploadedFileRow: FC<{ file: AgentUploadedFile }> = ({ file }) => {
  const getAuthHeader = useAuthHeader();
  const raw = getAuthHeader();
  const token = typeof raw === "string" && raw ? raw.replace(/^Bearer\s+/i, "") : null;
  const { currentThreadId } = useAgentsChat();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (!currentThreadId || !token || isDownloading) return;
    setIsDownloading(true);
    try {
      const upload = await createUploadedFileLink({ threadId: currentThreadId, filename: file.filename, token });
      openDownloadUrl(upload.url, upload.filename ?? file.display_name);
    } catch (error) {
      console.error("Failed to download uploaded file", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <File.Root className="max-w-full">
      <File.Icon mimeType={file.mime_type} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <File.Name>{file.display_name}</File.Name>
        <File.Size bytes={file.size} className="text-xs" />
      </div>
      <File.Download
        href="#"
        filename={file.display_name}
        aria-label={`Download ${file.display_name}`}
        className={cn(isDownloading || !currentThreadId || !token ? "pointer-events-none opacity-50" : undefined)}
        onClick={handleDownload}
      />
    </File.Root>
  );
};
